import { Product } from "@/types/enumerations";
import { TFunction } from "i18next";
import dayjs from "dayjs";
import { DateFormat } from "@/constants/common";
import { CommodityEnum, Contract } from "@/types/contract";
import { Process } from "@/types/contract";
import { CustomerTypeValues } from "@/constants/customer";

export const getContractDuration = (process: Process, product: Product, t: TFunction<"common">) => {
  const indefinitely = dayjs(new Date("9999-12-31").getTime());
  const priceValidToDate = dayjs.unix(product.value.pricelistValidTo);

  if (process === Process.VRB) {
    return t("product.fixedPeriodWithExtension");
  }
  if (product.value.pricelistValidMonth !== 0) {
    return t("product.monthsWithCount", { count: product.value.pricelistValidMonth });
  }
  if (priceValidToDate.isSame(indefinitely)) {
    return t("product.indefinitely");
  }
  return t("product.until", { date: priceValidToDate.format(DateFormat) });
};

export const makeProductIdWithoutCustomerType = (productId: string) => {
  if (!productId) {
    return "";
  }
  if (productId.includes("_")) {
    return productId;
  }
  const lastChar = productId[productId.length - 1]!;
  if (["1", "2"].includes(lastChar)) {
    return productId.substring(0, productId.length - 1);
  }

  return productId;
};

export const isOverFlowContract = (productId?: string | null) => {
  return Boolean(productId?.includes("_P_"));
};

export const isHighProductionContract = (productId?: string | null) => {
  return Boolean(productId?.includes("_NAD50_"));
};

export const getProduct = (
  commodity: CommodityEnum,
  process: Process,
  customerType: string,
  contractYear: number | null,
  products: Product[],
  productId: string | null
) => {
  const validProducts = getProducts(commodity, process, customerType, contractYear, products);
  if (!productId && process === Process.NEW_POINT) {
    return null;
  }

  if (!productId) {
    return getProducts(commodity, process, customerType, contractYear, products)[0];
  }

  if (process === Process.VRB) {
    const availableProducts = getProducts(commodity, process, customerType, contractYear, products);
    const isCurrentContractOverflow = isOverFlowContract(productId);
    const isCurrentContractHighProduction = isHighProductionContract(productId);
    if (isCurrentContractOverflow) {
      return availableProducts.find(product => isOverFlowContract(product.id)) ?? availableProducts[0];
    }
    if (isCurrentContractHighProduction) {
      return availableProducts.find(product => isHighProductionContract(product.id)) ?? availableProducts[0];
    } else {
      return availableProducts[0];
    }
  }

  if (process === Process.MOBILITY_SERVICES) {
    const availableProducts = getProducts(commodity, process, customerType, contractYear, products);
    return availableProducts[0];
  }

  return validProducts.find(
    product => makeProductIdWithoutCustomerType(product.id) === makeProductIdWithoutCustomerType(productId)
  );
};

const isYearContract = (contractId: string | null) => {
  return Boolean(contractId?.match(/_20\d{2}$/));
};

const getProducts = (
  commodity: CommodityEnum,
  process: string,
  customerType: string | string[],
  contractYear: number | null,
  products: Product[]
) => {
  const customerTypeArr = typeof customerType === "string" ? [customerType] : customerType;
  const validContracts = products.filter(
    p => p.value.commodity === commodity && p.processes.includes(process) && customerTypeArr.includes(p.value.customerType)
  );
  const desiredEnd = "_" + contractYear;
  if (!contractYear) {
    return validContracts;
  }

  return validContracts.filter(contract => !isYearContract(contract.id) || contract.id.endsWith(desiredEnd));
};

export const isProductIsValidForCustomerType = (
  commodity: CommodityEnum,
  process: Process,
  customerTypes: string | string[],
  productId: string | null,
  products: Product[]
) => {
  if (!productId) {
    return true;
  }
  if (process === Process.VRB) {
    return true;
  }
  if (process === Process.MOBILITY_SERVICES) {
    return !customerTypes.includes(CustomerTypeValues.Tradesman);
  }

  const validProducts = getProducts(commodity, process, customerTypes, null, products);
  const processedProductId = makeProductIdWithoutCustomerType(productId);
  return validProducts.some(product => makeProductIdWithoutCustomerType(product.id) === processedProductId);
};

export const getCurrentProductName = (contract: Contract, products: Product[]) => {
  const product = products.find(enumProduct => enumProduct.id === contract.contractDetails?.productId);

  if (!product) {
    return "";
  }

  if (product.fictive) {
    return contract.previousProduct?.name ?? "";
  }

  return product.name;
};
